import React, {useEffect, useMemo, useState} from "react";
import { useSearchParams } from "react-router-dom";
import AppLayout from "@amzn/awsui-components-react/polaris/app-layout";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import {
  Box,
  BreadcrumbGroup,
  ColumnLayout,
  Container,
  ContentLayout,
  Header,
  Spinner,
} from "@amzn/awsui-components-react";
import {Board, BoardItem, BoardProps} from "@amzn/awsui-board-components";
import DarkModeContext from "../../contexes/DarkModeContext";
import { StageContext } from "../../contexes/OrderIdContext";
import { UATProject } from "../../types/UATProject";
import { fetchUATProject } from "../../helpers/RavenUtils";
import { SummaryItem } from "../summary/SummaryItem";
import TestRunsView from "./TestRunsView";
import TestStatusHistoryView from "./TestStatusHistoryView";
import {BoardItemDefinition} from "@amzn/awsui-board-components/internal/interfaces";

interface BoardItemData {
  title: string;
  content: React.ReactNode | null;
}

interface BoardItemType {
  id: string;
  rowSpan: number;
  columnSpan: number;
  data: BoardItemData;
  widget?: React.ReactNode;
}

interface UATProjectViewProps {
  projectId: string;
}

export default function UATProjectView({ projectId }: UATProjectViewProps) {
  const initialItems: BoardItemDefinition<BoardItemData>[] = useMemo(() => [
    {
      id: "1",
      rowSpan: 6,
      columnSpan: 4,
      minRowSpan: 3,
      maxRowSpan: 12,
      minColumnSpan: 2,
      maxColumnSpan: 6,
      data: {
        title: "Test Runs",
        content: null
      }
    },
    {
      id: "2",
      rowSpan: 4,
      columnSpan: 2,
      minRowSpan: 3,
      maxRowSpan: 12,
      minColumnSpan: 2,
      maxColumnSpan: 6,
      data: {
        title: "Test Status History",
        content: null
      }
    }
  ], []);
  const [items, setItems] = useState<BoardItemDefinition<BoardItemData>[]>(initialItems);
  const [project, setProject] = useState<UATProject | undefined>(undefined);
  const [searchParams] = useSearchParams();
  const [darkMode, setDarkMode] = useState(false);
  const stageParam = searchParams.get("stage") || "Prod";

  // Initialize items
  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  // Initialize items
  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

// Fetch project data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const projectData = await fetchUATProject(projectId);
        setProject(projectData);
      } catch (error) {
        console.error('Error fetching project:', error);
      }
    };

    fetchData();
  }, [projectId]);

  // Update items when project changes
  useEffect(() => {
    if (project) {
      const updatedItems: BoardItemDefinition<BoardItemData>[] = [
        {
          ...initialItems[0],
          data: {
            title: "Test Runs",
            content: <TestRunsView project={project}/>
          }
        },
        {
          ...initialItems[1],
          data: {
            title: "Test Status History",
            content: <TestStatusHistoryView project={project}/>
          }
        }
      ];
      setItems(updatedItems);
    }
  }, [project, initialItems]);

  // Move createAnnouncement outside i18nStrings
  function createAnnouncement(
      operationAnnouncement: string,
      conflicts: BoardItemType[],
      disturbed: BoardItemType[]
  ) {
    const conflictsAnnouncement =
        conflicts.length > 0
            ? `Conflicts with ${conflicts.map((c) => c.data.title).join(", ")}.`
            : "";
    const disturbedAnnouncement =
        disturbed.length > 0 ? `Disturbed ${disturbed.length} items.` : "";
    return [operationAnnouncement, conflictsAnnouncement, disturbedAnnouncement]
        .filter(Boolean)
        .join(" ");
  }



  return (
      <DarkModeContext.Provider value={darkMode}>
        <StageContext.Provider value={stageParam}>
          {!project && (
              <Box textAlign="center">
                  <Spinner size="big"/>
              </Box>
            )}
            {project && (
              <AppLayout
                  contentType="default"
                  headerSelector="#h"
                  maxContentWidth={1700}
                  navigationHide
                  toolsHide
                  content={
                    <SpaceBetween size="xl">
                      <ContentLayout defaultPadding>
                        <SpaceBetween size="xl">
                          <BreadcrumbGroup
                              items={[
                                { text: "UAT Projects", href: "/uat" },
                                { text: project.name, href: `/project/${projectId}` }
                              ]}
                              ariaLabel="Breadcrumbs"
                          />
                          <Container
                              header={
                                <SpaceBetween size="m">
                                  <Header variant="h1">{project.name}</Header>
                                  <Header variant="h3">{project.description}</Header>
                                  <ColumnLayout columns={4} borders="vertical">
                                    <SummaryItem label="GL" value={project.GLPG}/>
                                    <SummaryItem label="Country" value={project.marketplace}/>
                                    <SummaryItem label="Status" value={project.status} />
                                    <SummaryItem label="Date" value={project.created_at} />
                                  </ColumnLayout>
                                </SpaceBetween>
                              }
                          >
                            <SpaceBetween size="xl">
                              <Board
                                  renderItem={(item) => (
                                      <BoardItem
                                          header={<Header>{item.data.title}</Header>}
                                          i18nStrings={{
                                            dragHandleAriaLabel: "Drag handle",
                                            dragHandleAriaDescription:
                                                "Use Space or Enter to activate drag, arrow keys to move, Space or Enter to submit, or Escape to discard. Be sure to temporarily disable any screen reader navigation feature that may interfere with the functionality of the arrow keys.",
                                            resizeHandleAriaLabel: "Resize handle",
                                            resizeHandleAriaDescription:
                                                "Use Space or Enter to activate resize, arrow keys to move, Space or Enter to submit, or Escape to discard. Be sure to temporarily disable any screen reader navigation feature that may interfere with the functionality of the arrow keys.",
                                          }}
                                      >
                                        {item.data.content}
                                      </BoardItem>
                                  )}
                                  // @ts-ignore
                                  onItemsChange={(event: {
                                    detail: { items: any[] };
                                  }) => setItems(event.detail.items)}
                                  items={items}
                                  i18nStrings={{
                                    liveAnnouncementDndStarted: (
                                        operationAnnouncement: string
                                    ) => operationAnnouncement,
                                    // @ts-ignore
                                    liveAnnouncementDndItemReordered: (
                                        operationAnnouncement: string
                                    ) => operationAnnouncement,
                                    liveAnnouncementDndItemCommitted: (
                                        operationAnnouncement: string,
                                        conflicts: Array<{ data: { title: string } }>,
                                        disturbed: any[]
                                    ) => {
                                      const conflictsAnnouncement =
                                          conflicts.length > 0
                                              ? `Conflicts with ${conflicts
                                                  .map((c) => c.data.title)
                                                  .join(", ")}.`
                                              : "";
                                      const disturbedAnnouncement =
                                          disturbed.length > 0
                                              ? `Disturbed ${disturbed.length} items.`
                                              : "";
                                      return [
                                        operationAnnouncement,
                                        conflictsAnnouncement,
                                        disturbedAnnouncement,
                                      ]
                                          .filter(Boolean)
                                          .join(" ");
                                    },
                                  }}
                              />
                            </SpaceBetween>
                          </Container>
                        </SpaceBetween>
                      </ContentLayout>
                    </SpaceBetween>
                  }
              />
            )}
        </StageContext.Provider>
      </DarkModeContext.Provider>
      );
}
